import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Gift, ArrowRight, Lock, Users, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#33264A] bg-[#1A1222] p-4">
      <p className="text-sm text-[#A49CB8]">{label}</p>
      <p className="text-2xl text-[#EDE6F7] font-semibold mt-1" data-testid={testid}>{value}</p>
    </div>
  );
}

function Card({ icon: Icon, title, children, to, cta, testid }) {
  return (
    <section className="glass-card p-6 sm:p-8 flex flex-col gap-5" data-testid={testid}>
      <h2 className="flex items-center gap-3 font-serif text-2xl text-[#EDE6F7]"><Icon className="h-6 w-6 text-[#B57BFF]" aria-hidden="true" />{title}</h2>
      {children}
      <Link to={to} data-testid={`${testid}-link`} className="mt-auto inline-flex items-center gap-2 self-start rounded-full bg-[#8B2CFF] px-5 py-2.5 text-white font-semibold hover:bg-[#9B45FF] transition-colors">
        {cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </section>
  );
}

export default function AdminHome() {
  const [mail, setMail] = useState(null);
  const [gifts, setGifts] = useState(null);
  const [error, setError] = useState("");
  const { user } = useAuth();
  useEffect(() => {
    api.get("/admin/email-log").then((r) => setMail(r.data)).catch((e) => setError(apiError(e)));
    api.get("/admin/gifts").then((r) => setGifts(r.data)).catch((e) => setError(apiError(e)));
  }, []);
  const sold = gifts ? gifts.gifts.length : 0;
  const redeemed = gifts ? gifts.gifts.filter((g) => g.state === "redeemed").length : 0;
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="admin-home">
        <div>
          <p className="eyebrow mb-2">Administrator</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7]">Admin dashboard</h1>
          {gifts && !gifts.can_switch && (
            <p className="mt-3 inline-flex items-center gap-2 text-[#C9B6FF]" data-testid="admin-home-readonly"><Lock className="h-4 w-4" aria-hidden="true" />Read-only access. You can view, but not change settings.</p>
          )}
        </div>
        {error && <p className="text-[#E6CCF2]" data-testid="admin-home-error">{error}</p>}
        <div className="grid gap-6 md:grid-cols-2">
          <Card icon={Mail} title="Email delivery" to="/admin/emails" cta="Open email delivery log" testid="admin-home-emails">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Accepted" value={mail ? mail.accepted_count : "…"} testid="admin-home-emails-accepted" />
              <Stat label="Failed" value={mail ? mail.failed.length : "…"} testid="admin-home-emails-failed" />
              <Stat label="Waiting" value={mail ? mail.pending.length : "…"} testid="admin-home-emails-pending" />
            </div>
          </Card>
          <Card icon={Gift} title="Gift vouchers" to="/admin/emails#gifts" cta={gifts?.can_switch ? "Manage gift vouchers" : "View gift vouchers"} testid="admin-home-gifts">
            <p className={`text-lg font-semibold ${gifts?.enabled ? "text-[#C9B6FF]" : "text-[#A49CB8]"}`} data-testid="admin-home-gifts-status">
              {!gifts ? "…" : gifts.enabled ? "ON: customers can buy and redeem gifts" : "OFF: hidden from customers"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Gifts sold" value={gifts ? sold : "…"} testid="admin-home-gifts-sold" />
              <Stat label="Redeemed" value={gifts ? redeemed : "…"} testid="admin-home-gifts-redeemed" />
            </div>
          </Card>
          {!user.readonly && (
            <Card icon={Users} title="Users & access" to="/admin/users" cta="Manage users" testid="admin-home-users">
              <p className="text-[#B9B3C8]">Invite read-only admins, disable access, sign users out and send secure password links. See the audit log.</p>
            </Card>
          )}
          <Card icon={ShieldCheck} title="Your security" to="/admin/security" cta="Open security settings" testid="admin-home-security">
            <p className="text-[#B9B3C8]">Two-step verification, recovery codes and signing out other devices.</p>
          </Card>
        </div>
      </main>
    </div>
  );
}
